import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Task, getTaskStage, getTaskModule } from '@orbit/shared/content-hierarchy';
import { getStageColor } from '@orbit/shared/constants';
import { BackToHome, ConfirmationModal } from '../index';
import { useAuth } from '@orbit/shared/auth';
import { updateTaskProgress, getBusinessIdea } from '@orbit/shared/business-ideas';
import { IconButton, Tooltip, Chip, Typography } from '@mui/material';
import { CheckCircle, RadioButtonUnchecked, Cancel } from '@mui/icons-material';
import { useBusinessIdea } from '../../hooks/useBusinessIdea';
import BusinessIdeaViewer from '../BusinessIdeaViewer';

interface TaskComponentFactoryProps {
  task: Task;
  businessIdeaId: string;
  onComplete?: (taskId: string, data: any) => void;
  onSave?: (taskId: string, data: any) => void;
}

const TaskComponentFactory: React.FC<TaskComponentFactoryProps> = ({
  task,
  businessIdeaId,
  onComplete,
  onSave,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { businessIdea } = useBusinessIdea();
  const [isCompleted, setIsCompleted] = React.useState(false);
  const [showConfirmationDialog, setShowConfirmationDialog] = React.useState(false);
  const [isUpdating, setIsUpdating] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isHovered, setIsHovered] = React.useState(false);

  // Load current task status from Firestore on component mount
  React.useEffect(() => {
    const loadTaskStatus = async () => {
      if (!user || !businessIdeaId) {
        setIsLoading(false);
        return;
      }

      try {
        const businessIdea = await getBusinessIdea(businessIdeaId);
        const taskProgress = businessIdea?.taskProgress?.[task.id];
        if (
          taskProgress &&
          taskProgress.userId === user.id &&
          taskProgress.status === 'completed'
        ) {
          setIsCompleted(true);
        }
      } catch (error) {
        console.error('Error loading task status:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadTaskStatus();
  }, [user, businessIdeaId, task.id]);

  const handleToggleCompletion = () => {
    setShowConfirmationDialog(true);
  };

  const handleConfirmUpdate = async () => {
    if (!user) return;

    setIsUpdating(true);
    try {
      const newStatus = isCompleted ? 'not_started' : 'completed';
      await updateTaskProgress(businessIdeaId, task.id, user.id, newStatus, {
        status: newStatus,
        updatedAt: new Date().toISOString(),
      });

      console.log(`Task ${task.id} status updated to: ${newStatus}`);
      setShowConfirmationDialog(false);
      setIsCompleted(!isCompleted);

      // Call onComplete if task was marked as completed
      if (newStatus === 'completed' && onComplete) {
        onComplete(task.id, { status: newStatus });
      }
    } catch (error) {
      console.error('Error updating task status:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background-dark">
        <BackToHome />
        <main className="p-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-background-surface/95 backdrop-blur-sm rounded-lg border border-gray-600 p-6">
              <div className="animate-pulse">
                <div className="h-8 bg-gray-700 rounded w-1/3 mb-4"></div>
                <div className="h-6 bg-gray-700 rounded w-1/2 mb-6"></div>
                <div className="h-4 bg-gray-700 rounded w-full mb-2"></div>
                <div className="h-4 bg-gray-700 rounded w-5/6"></div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-dark">
      <BackToHome />
      <main className="p-6">
        <div className="max-w-4xl mx-auto">
          {/* Task Header */}
          <div className="bg-background-surface/95 backdrop-blur-sm rounded-lg border border-gray-600 p-6 mb-6">
            {/* Title and Status Row */}
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-3xl font-bold text-white">{task.title}</h1>

              {/* Task Completion Toggle */}
              <div className="flex items-center gap-3">
                <Chip
                  label={isCompleted ? 'Completed' : 'Not Started'}
                  className={`text-white ${isCompleted ? 'bg-green-600' : 'bg-gray-600'}`}
                  size="small"
                />
                <Tooltip
                  title={isCompleted ? 'Mark as Incomplete' : 'Mark as Complete'}
                  placement="top"
                >
                  <IconButton
                    onClick={handleToggleCompletion}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    className={`w-10 h-10 transition-all duration-200 ${
                      isCompleted
                        ? 'text-green-500 hover:text-red-500 hover:bg-red-500/20'
                        : 'text-gray-400 hover:text-green-500 hover:bg-green-500/20'
                    }`}
                    size="small"
                  >
                    {isCompleted && !isHovered && (
                      <CheckCircle className="text-green-500" sx={{ fontSize: 24 }} />
                    )}
                    {isCompleted && isHovered && (
                      <RadioButtonUnchecked className="text-red-500" sx={{ fontSize: 24 }} />
                    )}
                    {!isCompleted && !isHovered && (
                      <RadioButtonUnchecked className="text-gray-400" sx={{ fontSize: 24 }} />
                    )}
                    {!isCompleted && isHovered && (
                      <CheckCircle className="text-green-500" sx={{ fontSize: 24 }} />
                    )}
                  </IconButton>
                </Tooltip>
              </div>
            </div>

            <p className="text-text-secondary text-lg">{task.description}</p>
            {/* Tags Row */}
            <div className="flex items-center gap-2 mt-4">
              <span
                className={`text-sm text-white px-3 py-1 rounded-full ${getStageColor(getTaskStage(task.id) || 'explore')}`}
              >
                {getTaskStage(task.id) || 'Explore'}
              </span>
              <span className="text-sm text-text-secondary bg-gray-700 px-3 py-1 rounded-full">
                {getTaskModule(task.id) || 'Ideation'}
              </span>
              {task.premium && (
                <span className="text-sm text-yellow-400 bg-yellow-900/20 px-3 py-1 rounded-full border border-yellow-500">
                  Premium
                </span>
              )}
            </div>
          </div>

          {/* Task-Specific Components */}
          {(() => {
            const taskComponents: Record<
              string,
              React.LazyExoticComponent<React.ComponentType<any>>
            > = {
              'validate-market-size': React.lazy(
                () => import('./explore/ideation/ValidateMarketSizeTask'),
              ),
            };

            const TaskComponent = taskComponents[task.id];

            if (TaskComponent) {
              return (
                <React.Suspense
                  fallback={
                    <div className="text-center py-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                      <Typography variant="h6" className="text-white">
                        Loading {task.title}...
                      </Typography>
                    </div>
                  }
                >
                  <TaskComponent
                    task={task}
                    businessIdeaId={businessIdeaId}
                    onComplete={onComplete}
                    onSave={onSave}
                  />
                </React.Suspense>
              );
            }

            // Fallback to default content
            return (
              <div className="bg-background-surface/95 backdrop-blur-sm rounded-lg border border-gray-600 p-6">
                {task.id === 'select-idea' && businessIdea ? (
                  <BusinessIdeaViewer
                    businessIdea={businessIdea}
                    readOnly={false}
                    onSave={(updatedBusinessIdea) => {
                      console.log('Business idea updated:', updatedBusinessIdea);
                      if (onSave) {
                        onSave(task.id, updatedBusinessIdea);
                      }
                    }}
                    onCancel={() => navigate('/home')}
                    showActions={true}
                    isOnboarding={false}
                  />
                ) : (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-6">🚧</div>
                    <h2 className="text-2xl font-bold text-white mb-4">
                      Task Implementation Coming Soon
                    </h2>
                    <p className="text-text-secondary mb-6 max-w-2xl mx-auto">
                      This task ({task.id}) will have its own dedicated component with forms,
                      interactive elements, and progress tracking. The implementation is planned and
                      will be developed based on user feedback and requirements.
                    </p>

                    <div className="bg-gray-800 rounded-lg p-4 text-left max-w-4xl mx-auto">
                      <h3 className="text-lg font-semibold text-white mb-3">Task Details:</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-text-secondary">Task ID:</span>
                          <span className="text-white ml-2 font-mono">{task.id}</span>
                        </div>
                        <div>
                          <span className="text-text-secondary">Sort Order:</span>
                          <span className="text-white ml-2">{task.sort}</span>
                        </div>
                        <div>
                          <span className="text-text-secondary">Business Idea ID:</span>
                          <span className="text-white ml-2 font-mono">{businessIdeaId}</span>
                        </div>
                        <div>
                          <span className="text-text-secondary">
                            Individual Completion Required:
                          </span>
                          <span className="text-white ml-2">
                            {task.individualCompletionRequired ? 'Yes' : 'No'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </main>

      {/* Confirmation Modal */}
      <ConfirmationModal
        open={showConfirmationDialog}
        onClose={() => setShowConfirmationDialog(false)}
        onConfirm={handleConfirmUpdate}
        title={`Confirm Task ${isCompleted ? 'Incomplete' : 'Completion'}`}
        subtitle={`Are you sure you want to mark "${task.title}" as ${isCompleted ? 'incomplete' : 'complete'}?`}
        confirmText="Save"
        cancelText="Cancel"
        isLoading={isUpdating}
        confirmButtonColor={isCompleted ? 'gray' : 'blue'}
      />
    </div>
  );
};

export default TaskComponentFactory;
